import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { EmptyState } from 'components/empty-state';
import { PlainLink } from 'components/link';
import { SkeletonTableRows } from 'components/skeleton-table-rows';
import { PaginatedTable } from 'components/table/paginated-table';
import noDataSvg from 'images/srp-illustration-empty-search.svg';
import type { SalesforceAccountResponse } from 'types/api-responses';
import { EMPTY_CELL } from 'utils/empty-cell';
import { formatBillingAddress } from '../utils';
import style from './link-crm-account-modal.module.scss';

// The asset shim types images as any; Vite resolves them to their URL.
const NO_DATA_ICON = noDataSvg as string;

interface AccountSearchTableProps {
  searchText: string;
  searchData: SalesforceAccountResponse[];
  searchTotalCount: number;
  searchPageSize: number;
  searchPage: number;
  isLoading: boolean;
  hoveredAccountId: string | null;
  onAccountSelect: (
    account: SalesforceAccountResponse,
    checked: boolean
  ) => void;
  onHoverAccount: (accountId: string | null) => void;
  onPaginate: (page: number, pageSize: number) => void;
  isAccountSelected: (accountId: string) => boolean;
  isAccountAlreadyLinked: (accountId: string) => boolean;
}

/**
 * The CRM account search results, with a pick box per account.
 * @param {AccountSearchTableProps} props - The table's data and handlers
 * @return {JSX.Element} The table
 */
export function AccountSearchTable({
  searchText,
  searchData,
  searchTotalCount,
  searchPageSize,
  searchPage,
  isLoading,
  hoveredAccountId,
  onAccountSelect,
  onHoverAccount,
  onPaginate,
  isAccountSelected,
  isAccountAlreadyLinked,
}: AccountSearchTableProps) {
  if (searchText.trim().length === 0) {
    return (
      <div className={style.emptyStateContainer}>
        <EmptyState
          icon={NO_DATA_ICON}
          headerText="Please search for CRM accounts"
          helperText="Enter a search term in the search bar above to find CRM accounts"
        />
      </div>
    );
  }

  return (
    <PaginatedTable
      paginationOnTop={false}
      persistentPagination={false}
      pageSize={searchPageSize}
      pageSizes={[16, 24, 48, 64]}
      page={searchPage}
      totalItems={searchTotalCount}
      isLoading={isLoading}
      loadingComponent={
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader></TableHeader>
              <TableHeader>CRM Account Name</TableHeader>
              <TableHeader>Type</TableHeader>
              <TableHeader>Billing Address</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            <SkeletonTableRows rows={searchPageSize} columns={4} />
          </TableBody>
        </Table>
      }
      emptyStateComponent={
        <div className={style.emptyStateContainer}>
          <EmptyState
            icon={NO_DATA_ICON}
            headerText="No CRM Accounts Found"
            helperText="Try adjusting your search criteria"
          />
        </div>
      }
      onPaginate={({ page, pageSize: newPageSize }) => {
        onPaginate(page, newPageSize);
      }}
    >
      <TableHead>
        <TableRow>
          <TableHeader></TableHeader>
          <TableHeader>CRM Account Name</TableHeader>
          <TableHeader>Type</TableHeader>
          <TableHeader>Billing Address</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {searchData.map((account) => {
          const isAlreadyLinked = isAccountAlreadyLinked(account.salesforceId);
          const isSelected = isAccountSelected(account.salesforceId);

          return (
            <TableRow key={account.salesforceId}>
              <TableCell>
                <div
                  className={style.checkboxWrapper}
                  onMouseEnter={() => {
                    if (isAlreadyLinked) {
                      onHoverAccount(account.salesforceId);
                    }
                  }}
                  onMouseLeave={() => {
                    onHoverAccount(null);
                  }}
                >
                  <Checkbox
                    id={`select-account-${account.salesforceId}`}
                    labelText=""
                    checked={isSelected}
                    disabled={isAlreadyLinked}
                    onChange={(_evt, { checked }: { checked: boolean }) => {
                      onAccountSelect(account, checked);
                    }}
                  />
                  {isAlreadyLinked &&
                    hoveredAccountId === account.salesforceId && (
                      <div className={style.tooltip}>
                        CRM Account is already linked
                      </div>
                    )}
                </div>
              </TableCell>
              <TableCell>
                <PlainLink
                  href={account.salesforceHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {account.name}
                </PlainLink>
              </TableCell>
              <TableCell>{account.type || EMPTY_CELL}</TableCell>
              <TableCell>
                {formatBillingAddress(account.billingAddress)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </PaginatedTable>
  );
}
